import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, ArrowUpRight, MessageCircle } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Reveal, Tag } from "@/components/portfolio/Reveal";
import { CONTACT, WA_MAIN } from "@/data/content";

const endpoint = process.env.REACT_APP_FORMSPREE_ENDPOINT;

export const Contact = () => {
  const { t } = useLang();
  const c = t.contact;
  const f = c.form;
  const [values, setValues] = useState({ name: "", email: "", message: "", _gotcha: "" });
  const [state, setState] = useState({ status: "idle", message: "" });

  const update = (e) => setValues((v) => ({ ...v, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!values.name.trim() || !values.email.trim() || !values.message.trim()) {
      setState({ status: "error", message: f.required });
      return;
    }
    if (values._gotcha.trim()) return;
    if (!endpoint) {
      // Sin Formspree configurado: abrir el correo del visitante con el mensaje listo
      const subject = encodeURIComponent(`Contacto web — ${values.name.trim()}`);
      const body = encodeURIComponent(`${values.message.trim()}\n\n${values.name.trim()} · ${values.email.trim()}`);
      window.location.href = `mailto:${CONTACT.email}?subject=${subject}&body=${body}`;
      setState({ status: "success", message: f.mailto || f.success });
      return;
    }
    setState({ status: "submitting", message: "" });
    try {
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({ name: values.name.trim(), email: values.email.trim(), message: values.message.trim(), _gotcha: "" }),
      });
      if (!res.ok) throw new Error();
      setValues({ name: "", email: "", message: "", _gotcha: "" });
      setState({ status: "success", message: f.success });
    } catch {
      setState({ status: "error", message: f.error });
    }
  };

  const inputCls = "w-full rounded-xl border border-black/10 bg-mist px-4 py-3.5 text-ink placeholder:text-ink/40 focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/40 transition-all duration-200";

  const directs = [
    { testid: "contact-email-link", icon: Mail, label: c.emailLabel, value: CONTACT.email, href: `mailto:${CONTACT.email}`, ext: false },
    { testid: "contact-wa-ec", icon: MessageCircle, label: c.phoneLabel, value: CONTACT.phone, href: CONTACT.phoneWa, ext: true },
    { testid: "contact-wa-us", icon: MessageCircle, label: c.phone2Label, value: CONTACT.phone2, href: CONTACT.phone2Wa, ext: true },
  ];

  return (
    <section id="contact" data-testid="contact-section" className="bg-paper py-24 md:py-32">
      <div className="max-w-[88rem] mx-auto px-5 md:px-10">
        <Reveal className="flex flex-col gap-5 mb-14 max-w-2xl">
          <Tag>{c.tag}</Tag>
          <h2 className="font-extrabold text-ink text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02]">{c.title}</h2>
          <p className="font-normal text-base md:text-lg text-ink/65">{c.subtitle}</p>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Form */}
          <Reveal>
            <form onSubmit={submit} noValidate data-testid="contact-form" className="flex flex-col gap-4">
              <input name="name" data-testid="form-name" value={values.name} onChange={update} placeholder={f.name} className={inputCls} autoComplete="name" />
              <input name="email" type="email" data-testid="form-email" value={values.email} onChange={update} placeholder={f.email} className={inputCls} autoComplete="email" />
              <textarea name="message" data-testid="form-message" value={values.message} onChange={update} placeholder={f.message} rows={6} className={inputCls} />
              <div className="absolute left-[-10000px]" aria-hidden="true">
                <input name="_gotcha" value={values._gotcha} onChange={update} tabIndex={-1} autoComplete="off" />
              </div>
              {state.message && (
                <p role="status" data-testid="form-status" className={`font-semibold text-sm ${state.status === "error" ? "text-red-600" : "text-green-600"}`}>
                  {state.message}
                </p>
              )}
              <button
                type="submit"
                data-testid="form-submit"
                disabled={state.status === "submitting"}
                className="mt-1 inline-flex items-center justify-center gap-2 font-bold text-base bg-ink text-white rounded-full px-8 py-4 hover:bg-brand hover:text-ink transition-colors duration-300 disabled:opacity-60 w-fit"
              >
                {state.status === "submitting" ? f.sending : f.send}
              </button>
            </form>
          </Reveal>

          {/* Direct contact */}
          <Reveal delay={0.1}>
            <div className="flex flex-col">
              <p className="font-bold text-sm uppercase tracking-[0.15em] text-ink/50 mb-2">{c.directTitle}</p>
              {directs.map((d, i) => {
                const Icon = d.icon;
                return (
                  <a
                    key={i}
                    data-testid={d.testid}
                    href={d.href}
                    target={d.ext ? "_blank" : undefined}
                    rel={d.ext ? "noopener noreferrer" : undefined}
                    className="group flex items-center justify-between py-5 border-b border-black/[0.08]"
                  >
                    <div className="flex items-center gap-4">
                      <Icon size={20} className="text-brand-dark" />
                      <div>
                        <p className="font-medium text-xs uppercase tracking-wide text-ink/50">{d.label}</p>
                        <p className="font-bold text-lg md:text-xl text-ink group-hover:text-brand-dark transition-colors duration-300">{d.value}</p>
                      </div>
                    </div>
                    <ArrowUpRight size={20} className="text-ink/40 group-hover:text-brand-dark group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300" />
                  </a>
                );
              })}

              <motion.a
                data-testid="contact-whatsapp-btn"
                href={WA_MAIN}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="mt-8 inline-flex items-center justify-center gap-3 font-bold text-base bg-whatsapp text-white rounded-full px-8 py-4 w-fit"
              >
                <MessageCircle size={20} /> {c.whatsapp}
              </motion.a>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
